import { NotificationTopicSchema } from '@sit/notifications-shared';
import { useGetTimesheet } from '@sit/openapi';
import { useLatestNotification } from '@web/components/Notifications/hooks/useLatestNotification';
import { useIsStaff } from '@web/containers/Timesheets/hooks';
import { sendPrintToIntacct } from '@web/services/intacct';
import { getIntacctMod } from '@web/services/intacct-mod';
import { useEffect, useState } from 'react';
import { usePostTimesheetSaveAsync } from './use-post-timesheet-save-async';

export function useSendPrintToIntacct({ staff: staffOverride }: { staff?: boolean } = {}) {
  const isStaffView = useIsStaff();
  const isStaff = staffOverride ?? isStaffView;
  const mod = getIntacctMod();
  const [timesheetId, setTimesheetId] = useState<string | null>(null);
  const [syncingToIntacct, setSyncingToIntacct] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { data: timesheetData } = useGetTimesheet(timesheetId ?? undefined);
  const { mutate: saveAsync } = usePostTimesheetSaveAsync();
  const syncResponse = useLatestNotification(NotificationTopicSchema.enum.SAVE_TIMESHEET);

  const recordNo = timesheetData?.recordNo;

  useEffect(() => {
    if (isRefreshing && recordNo) {
      setIsRefreshing(false);
    }
  }, [recordNo, isRefreshing]);

  useEffect(() => {
    if (timesheetId == null || syncingToIntacct || isRefreshing) return;
    if (!recordNo) {
      saveAsync({ pathParams: { id: timesheetId } });
      setSyncingToIntacct(true);
    } else {
      sendPrintToIntacct(recordNo, mod, isStaff);
      setTimesheetId(null);
    }
  }, [saveAsync, timesheetId, isStaff, recordNo, mod, syncingToIntacct, isRefreshing]);

  useEffect(() => {
    if (timesheetId == null || !syncingToIntacct || !syncResponse) return;
    const { data } = syncResponse;
    // We have to do this instead of the `useLatestNotification` params hook.
    if (!(data && 'timesheetId' in data && data.timesheetId === timesheetId)) return;
    setSyncingToIntacct(false);
    setIsRefreshing(true);
  }, [syncResponse, syncingToIntacct, timesheetId]);

  return ({ timesheetId: printTimesheetId }: { timesheetId: string }) => {
    setTimesheetId(printTimesheetId);
  };
}
